import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import {
  type ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import type { LightboxConfig } from 'ng-gallery/lightbox';
import { LIGHTBOX_CONFIG } from 'ng-gallery/lightbox';
import {
  authHttpInterceptorFn,
  AuthService as Auth0AuthService,
  provideAuth0,
} from '@auth0/auth0-angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors, HttpContext } from '@angular/common/http';
import {
  BackendErrorWithMessageError,
  DISABLE_HTTP_GLOBAL_ERROR_TO_BE_HANDLED,
  HandledErrorResponse,
  httpErrorInterceptor,
  provideHttpErrorOptions,
} from '@pct-ng/core/error-http';
import { CustomErrorStateMatcher } from '@pct-ng/form';
import { ErrorStateMatcher, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DATE_FNS_FORMATS, provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  defaultMatDialogProvider,
  type MessageResult,
  MessageService,
  openOKDialog,
} from '@pct-ng/feature-message-material';
import { provideToastr } from 'ngx-toastr';
import { hu } from 'date-fns/locale';
import { environment } from '@szebit-app/environment';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore, Store } from '@ngxs/store';
import { UserState } from './frontend/user/state/user.state';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoFallbackService } from './core/transloco/transloco-fallback.service';
import { TableState } from '@pct-ng/feature-table-material';
import { transformHttpErrorToPctHttpErrorResponse } from './core/error/http/transform-http-error-to-pct-http-error-response';
import { handleIdentifiedError } from './core/error/http/handle-identified-error';
import { AuthService } from '@szebit-app/sdk/backend';
import {
  catchError,
  filter,
  firstValueFrom,
  type Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { SetUserAction } from './frontend/user/state/action/set-user.action';
import { isNil } from '@pct-ts/typeguard';
import { DOCUMENT } from '@angular/common';
import { removeSplashScreen } from './remove-splashscreen';
import type { BackendErrorWithMessage } from './core/error/http/typeguard/is-backend-error-with-message';
import { provideServiceWorker } from '@angular/service-worker';

export const PWA_PROVIDERS = isDevMode()
  ? []
  : [
      provideServiceWorker('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:5000',
      }),
      provideFirebaseApp(() => initializeApp({ ...(environment.firebaseConfig ?? {}) })),
      provideMessaging(() => getMessaging()),
    ];

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'hu-HU',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: hu,
    },
    provideDateFnsAdapter(MAT_DATE_FNS_FORMATS),
    AuthService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptors([authHttpInterceptorFn, httpErrorInterceptor])),
    provideAuth0({
      ...environment.auth0,
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
    }),
    provideAnimationsAsync(),
    {
      provide: LIGHTBOX_CONFIG,
      useValue: {
        keyboardShortcuts: true,
        exitAnimationTime: 1000,
      } satisfies LightboxConfig,
    },
    provideHttpClient(withInterceptors([httpErrorInterceptor])),
    { provide: ErrorStateMatcher, useClass: CustomErrorStateMatcher },
    defaultMatDialogProvider,
    provideToastr({
      tapToDismiss: true,
      closeButton: true,
      preventDuplicates: true,
      countDuplicates: true,
      resetTimeoutOnDuplicate: true,
      includeTitleDuplicates: true,
      newestOnTop: true,
      progressBar: true,
      progressAnimation: 'decreasing',
      timeOut: 1000 * 10,
      positionClass: 'toast-top-right',
    }),
    provideStore(
      [UserState, TableState],
      { developmentMode: environment.isAngularDevMode },
      ...(environment.isAngularDevMode ? [withNgxsReduxDevtoolsPlugin()] : []),
    ),
    TranslocoFallbackService,
    { provide: TranslocoService, useExisting: TranslocoFallbackService },
    provideAppInitializer(() => {
      const store = inject(Store);
      const authService = inject(AuthService);
      // Start service
      inject(MessageService);
      const document = inject(DOCUMENT);
      const auth0Service = inject(Auth0AuthService);

      return firstValueFrom(
        auth0Service.isAuthenticated$.pipe(
          take(1),
          switchMap(isAuthenticated =>
            isAuthenticated
              ? authService
                  .getAuthUser(
                    new HttpContext().set(DISABLE_HTTP_GLOBAL_ERROR_TO_BE_HANDLED, {
                      codes: [403, 404],
                    }),
                  )
                  .pipe(
                    tap(user => store.dispatch(new SetUserAction(user))),
                    catchError(error => {
                      removeSplashScreen(document);

                      if (
                        error instanceof HandledErrorResponse &&
                        error.originalError instanceof BackendErrorWithMessageError
                      ) {
                        const originalError =
                          error.originalError as BackendErrorWithMessageError<BackendErrorWithMessage>;
                        let dialog$: Observable<MessageResult | undefined> | null = null;
                        if (
                          error.status === 403 &&
                          originalError.backendError.errors.find(
                            ({ strId }) => strId === 'NOT_ACCEPTED_REGISTRATION',
                          )
                        ) {
                          dialog$ = openOKDialog({
                            title: 'Belépés',
                            message: `Regisztrációja jelenleg ellenőrzés alatt!
                          Fiókja aktiválásáról e-mailben értesítjük, és ezt követően tud majd belépni.

                          Megértését köszönjük!`,
                          });
                        } else if (
                          error.status === 404 &&
                          originalError.backendError.errors.find(
                            ({ strId }) => strId === 'NOT_FOUND_AUTH0_USER',
                          )
                        ) {
                          dialog$ = openOKDialog({
                            title: 'Belépés',
                            message: `Felhasználói fiókja már nem érhető el, ezért biztonsági okokból kijelentkeztettük.

                            A nyilvános tartalmak továbbra is szabadon megtekinthetők.`,
                          });
                        }

                        if (dialog$ !== null) {
                          return dialog$.pipe(switchMap(() => auth0Service.logout()));
                        }
                      }
                      return throwError(() => error);
                    }),
                    filter(user => !isNil(user)),
                  )
              : of(null),
          ),
        ),
      );
    }),
    provideHttpErrorOptions({
      transformHttpErrorToPtHttpErrorResponseFn: transformHttpErrorToPctHttpErrorResponse,
      handleIdentifiedErrorFn: handleIdentifiedError,
    }),
    PWA_PROVIDERS,
  ],
};
